import {
  TextChart,
  TextChartMajor,
  TextChartMinor,
} from "components/widgets/ScorePage/widgets/TextCharts"
import PropTypes from "prop-types"
import React, { useCallback } from "react"
import { useTranslation } from "react-i18next"
import HelpWidgetChartInteractiveMolecule from "views/molecules/interactive/HelpWidgetChartInteractiveMolecule"
import Unit from "views/atoms/reports/Unit"
import ChartBadge from "views/atoms/badges/ChartBadge"
import CloudProviderChart from "./CloudProviderChart"
import ChartSDG13WaterConsumptionMolecule from "views/molecules/canvases/charts/websites/ChartSDG13WaterConsumptionMolecule"
import { useAppContext } from "components/AppContext"
import ScoreLayoutOrganism from "views/organisms/scores/ScoreLayoutOrganism"
import DonutChartTemplate from "views/molecules/charts/template/DonutChartTemplate"
import { convertValueToReadable } from "services/units/valueConversion"
import { M3_UNIT, VOLUME_UNITS_KEYS } from "services/units/units"
import { round } from "services/units/round"
import ChartWithBadgeWrapperMolecule from "views/molecules/charts/ChartWithBadgeWrapperMolecule"
import ScoreChartErrorBoundary from "components/ErrorBoundary/ScoreChartErrorBoundary"

const YEARLY_WATER_CONSUMPTION_PER_FTE_MIN = 4
const YEARLY_WATER_CONSUMPTION_PER_FTE_MAX = 7

const Objective6Score = ({ score, websitesScore, collect }) => {
  const { score: globalScore } = useAppContext()
  const { t } = useTranslation()

  const cloudProviders = score.cloud_providers

  const translationPrefix = "score:score.6."

  const chart1TrendBadge = globalScore?.trend_badges?.odd6_water_consumption
  const chart2TrendBadge = globalScore?.trend_badges?.odd6_water_consumption_reuse_maturity
  const chart5TrendBadge = globalScore?.trend_badges?.odd6_water_consumption_from_paper_use
  const maturityTrendBadge = globalScore?.trend_badges?.odd6_maturity

  const chart1 = useCallback(() => {
    const grey_water = Math.round(
      (score["odd6_water_consumption_fab"] / score["odd6_water_consumption"]) *
        100
    )

    const convertedValue = convertValueToReadable(
      score["odd6_water_consumption"],
      M3_UNIT,
      VOLUME_UNITS_KEYS,
      (value) => round(value, 1)
    )

    const content = {
      kpi: convertedValue.value,
      kpi_unit: <Unit unit={t(`units:units.volume.${convertedValue.unit}`)} />,
      kpi_text: t(translationPrefix + "chart1.text"),
      reference: grey_water,
      reference_unit: t(translationPrefix + "chart1.reference_unit"),
      reference_text: t(translationPrefix + "chart1.subtext"),
      translationPrefix_chartX: translationPrefix + "chart1.",
    }

    const content_for_display = <TextChartMajor content={content} />

    const chartContent = (
      <TextChart content={content_for_display} />
    )

    return (
      <HelpWidgetChartInteractiveMolecule
        text={t(translationPrefix + "chart1.help")}
      >
        <ChartWithBadgeWrapperMolecule trendBadge={chart1TrendBadge} color="auto">
          {chartContent}
        </ChartWithBadgeWrapperMolecule>
      </HelpWidgetChartInteractiveMolecule>
    )
  }, [score, chart1TrendBadge])

  const chart2 = useCallback(() => {
    if (!score?.has_internal_datacenter_inventories) {
      return <ScoreChartErrorBoundary />
    }

    const waterConsumptionReuseRatio = collect?.water_consumption_reuse_ratio

    const badge = (
      <ChartBadge
        value={waterConsumptionReuseRatio}
        lowerLimit={40}
        upperLimit={70}
      />
    )

    const content = {
      reference: badge,
      kpi: waterConsumptionReuseRatio,
      kpi_unit: t(translationPrefix + "chart2.unit"),
      kpi_text: t(translationPrefix + "chart2.text"),
    }

    const content_for_display = <TextChartMinor content={content} />

    const chartContent = (
      <TextChart content={content_for_display} />
    )

    return (
      <HelpWidgetChartInteractiveMolecule
        text={t(translationPrefix + "chart2.help")}
      >
        <ChartWithBadgeWrapperMolecule trendBadge={chart2TrendBadge} color="auto-inverse">
          {chartContent}
        </ChartWithBadgeWrapperMolecule>
      </HelpWidgetChartInteractiveMolecule>
    )
  }, [score, chart2TrendBadge, collect])

  // TODO: find water figures from clouders
  // TODO: fix references to config - for now points to ODD 7
  const chart3 = useCallback(() => {
    return <ScoreChartErrorBoundary />

    const hasCloud =
      cloudProviders &&
      (Array.isArray(cloudProviders)
        ? cloudProviders.length > 0
        : Object.keys(cloudProviders).length > 0)

    // TODO: Refine the logic for displaying ScoreChartErrorBoundary when no cloud
    if (!hasCloud) {
      return <ScoreChartErrorBoundary />
    }

    return <CloudProviderChart clouds={cloudProviders} />
  }, [cloudProviders])

  const chart4 = useCallback(() => {
    const chart4Score = score?.odd6_equipment_scores_top || {}
    const data = chart4Score.doughnut_charts?.map((dataset) => dataset.data)
    const label = chart4Score.doughnut_charts?.map((dataset) => dataset.label)

    return (
      <DonutChartTemplate
        title={t(translationPrefix + "chart4.title")}
        data={data}
        labelPath={"common:equipment_types"}
        labelKeys={label}
        centerValue={chart4Score.doughnut_charts_data_total?.number}
        centerValueUnit={chart4Score.doughnut_charts_data_total?.unit}
        unitPath={"units:units.volume"}
        unitKey={chart4Score.doughnut_charts_data_total?.unit}
        helpText={t(translationPrefix + "chart4.help")}
      />
    )
  }, [score])

  const chart5 = useCallback(() => {
    const water_consumption_from_paper_use =
      score["odd6_water_consumption_from_paper_use"]

    const hasImpression =
      water_consumption_from_paper_use != null &&
      Number(water_consumption_from_paper_use) > 0

    if (!hasImpression) {
      return <ScoreChartErrorBoundary />
    }

    const convertedValue = convertValueToReadable(
      water_consumption_from_paper_use,
      M3_UNIT,
      VOLUME_UNITS_KEYS,
      (value) => round(value, 1)
    )

    const employees = score.workforce
    let waterConsumptionFromPaperUsePerEmployees =
      employees > 0
        ? (water_consumption_from_paper_use / employees).toFixed(2)
        : water_consumption_from_paper_use

    const convertedSubValue = convertValueToReadable(
      waterConsumptionFromPaperUsePerEmployees,
      M3_UNIT,
      VOLUME_UNITS_KEYS,
      (value) => round(value, 1)
    )

    const lowerLimit =
      YEARLY_WATER_CONSUMPTION_PER_FTE_MIN * (score.period_months / 12)
    const upperLimit =
      YEARLY_WATER_CONSUMPTION_PER_FTE_MAX * (score.period_months / 12)

    const badge = (
      <ChartBadge
        value={Number(waterConsumptionFromPaperUsePerEmployees)}
        lowerLimit={lowerLimit}
        upperLimit={upperLimit}
        inverse
      />
    )

    const volumeUnit = t(`units:units.volume.${convertedSubValue.unit}`)
    const employeesLabel = t("score:score.6.employees")
    const sub_kpi_unit = `${volumeUnit} / ${employeesLabel}`

    const content = {
      reference: badge,
      kpi: convertedValue.value,
      kpi_unit: t(`units:units.volume.${convertedValue.unit}`),
      kpi_text: t(translationPrefix + "chart5.text"),
      sub_kpi: waterConsumptionFromPaperUsePerEmployees,
      sub_kpi_unit: <Unit unit={sub_kpi_unit} />,
    }

    const content_for_display = <TextChartMinor content={content} />

    const chartContent = (
      <TextChart content={content_for_display} />
    )

    return (
      <HelpWidgetChartInteractiveMolecule
        text={t(translationPrefix + "chart5.help")}
      >
        <ChartWithBadgeWrapperMolecule trendBadge={chart5TrendBadge} color="auto">
          {chartContent}
        </ChartWithBadgeWrapperMolecule>
      </HelpWidgetChartInteractiveMolecule>
    )
  }, [score, chart5TrendBadge])

  const chart6 = useCallback(() => {
    const hasWebsite = websitesScore?.water_consumption != null

    // TODO: Refine the logic for displaying ScoreChartErrorBoundary when no website
    if (!hasWebsite) {
      return <ScoreChartErrorBoundary />
    }

    return (
      <ChartSDG13WaterConsumptionMolecule
        mixedChart={websitesScore?.water_consumption}
      />
    )
  }, [websitesScore])

  return (
    <ScoreLayoutOrganism
      maturity={score["odd6_maturity"] ?? 0}
      maturityTrendBadge={maturityTrendBadge}
      chart1={chart1()}
      chart2={chart2()}
      chart3={chart3()}
      chart4={chart4()}
      chart5={chart5()}
      chart6={chart6()}
    />
  )
}

Objective6Score.propTypes = {
  score: PropTypes.object.isRequired,
  collect: PropTypes.object.isRequired,
  websitesScore: PropTypes.object,
}

export default Objective6Score